/**
 * Import thunks for the code-split sidebar destinations.
 *
 * Both `lazy()` in routes/index.tsx and the sidebar's hover/idle prefetch call
 * these same thunks. The dynamic-import module cache dedupes, so a chunk warmed
 * on hover is already resolved by the time `lazy()` reads it — the route then
 * commits without suspending at all, which is what makes navigation feel instant.
 *
 * /conversations is absent on purpose: it renders Dialogs, which routes/index.tsx
 * imports eagerly, so it is already in the main bundle with no chunk to warm.
 */
export const routePreload = {
  home: () => import('../screens/Home/Home'),
  initiatives: () => import('../screens/Initiatives/Initiatives'),
  teamView: () => import('../screens/TeamView/TeamView'),
  analytics: () => import('../screens/Analytics/Analytics'),
  admin: () => import('../screens/Admin/Admin'),
  settings: () => import('../screens/Settings'),
} as const

export type TRoutePreloadKey = keyof typeof routePreload

/**
 * Warmed once the browser is idle, so even a first click with no hover is
 * instant. Deliberately excludes `analytics`: that chunk plus its shared chart
 * chunk is ~1 MB (vs ~70 kB for all five below), too much to fetch speculatively
 * for every session. Analytics relies on hover/focus prefetch instead, backed by
 * the LoadingPage fallback that now renders immediately.
 */
const IDLE_WARM_KEYS: readonly TRoutePreloadKey[] = [
  'home',
  'initiatives',
  'teamView',
  'admin',
  'settings',
]

/**
 * Prefetch the light sidebar chunks on idle. Returns a cleanup that cancels the
 * pending callback.
 */
export const preloadSidebarRoutesOnIdle = (): (() => void) => {
  const warm = (): void => {
    IDLE_WARM_KEYS.forEach(key => {
      void routePreload[key]()
    })
  }

  // requestIdleCallback is unavailable in Safari — fall back to a macrotask.
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(warm)
    return (): void => window.cancelIdleCallback(handle)
  }

  const timer = window.setTimeout(warm, 2000)
  return (): void => window.clearTimeout(timer)
}
